import { useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { FilterField } from '@/components/common/FilterField';
import { FilterFieldsGrid } from '@/components/common/FilterFieldsGrid';
import { OptionSelect } from '@/components/common/OptionSelect';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import {
  classSubjectsQueryOptions,
  questionClassesQueryOptions,
  questionSubjectsInfiniteQueryOptions,
} from '../api/question.api';
import {
  getClassSubjectColumns,
  getQuestionClassColumns,
  getQuestionSubjectColumns,
} from '../components/QuestionColumns';
import { QuestionClassFormModal, QuestionSubjectFormModal } from '../components/QuestionFormModals';
import {
  useAssignSubject,
  useDeleteQuestionClass,
  useDeleteQuestionSubject,
  useUnassignSubject,
} from '../hooks/useQuestionMutations';
import type { ClassSubject, QuestionClass, QuestionSubject } from '../types/question.types';

type SubjectTabId = 'classes' | 'classSubjects' | 'subjects';

interface ClassPickerText {
  id: string;
  label: string;
  placeholder: string;
}

const CLASS_SUBJECTS_PICKER: ClassPickerText = {
  id: 'cs-class',
  label: 'Class',
  placeholder: 'Pick a class',
};

const PUT_ON_CLASS_PICKER: ClassPickerText = {
  id: 'qs-class',
  label: 'Put on class',
  placeholder: 'Pick a class first',
};

const route = getRouteApi('/_auth/question/subjects');

export function SubjectManagementPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const subjectSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const classes = useQuery(questionClassesQueryOptions());
  const subjects = useInfiniteTableQuery(
    questionSubjectsInfiniteQueryOptions({ search: search.search, isActive: search.isActive }),
  );
  const pairs = useQuery(classSubjectsQueryOptions(search.classId, true));

  const [activeTabId, setActiveTabId] = useState<SubjectTabId>(
    search.classId === undefined ? 'classes' : 'classSubjects',
  );
  const [editingClass, setEditingClass] = useState<QuestionClass | null>(null);
  const [isClassFormOpen, setClassFormOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<QuestionSubject | null>(null);
  const [isSubjectFormOpen, setSubjectFormOpen] = useState(false);
  const [removingClass, setRemovingClass] = useState<QuestionClass | null>(null);
  const [removingSubject, setRemovingSubject] = useState<QuestionSubject | null>(null);
  const [unassigning, setUnassigning] = useState<ClassSubject | null>(null);

  const deleteClass = useDeleteQuestionClass(() => setRemovingClass(null));
  const deleteSubject = useDeleteQuestionSubject(() => setRemovingSubject(null));
  const assign = useAssignSubject();
  const unassign = useUnassignSubject(() => setUnassigning(null));

  const selectedClass = (classes.data ?? []).find((c) => c.id === search.classId);

  const classQuery = (search.search ?? '').trim().replace(/\s+/gu, ' ').toLowerCase();
  const filteredClasses = (classes.data ?? []).filter((c) => {
    if (classQuery === '') return true;
    return [c.name, c.nameLocal, c.code, c.description].some(
      (value) => value?.toLowerCase().includes(classQuery) ?? false,
    );
  });

  function handleClassOpen(questionClass: QuestionClass) {
    void navigate({ search: (prev) => ({ ...prev, classId: questionClass.id }) });
    setActiveTabId('classSubjects');
  }

  const tabs: TableTab<SubjectTabId>[] = [
    { id: 'classes', label: 'Classes', count: filteredClasses.length },
    {
      id: 'classSubjects',
      label: 'Class subjects',
      count: selectedClass === undefined ? 0 : pairs.data?.length,
      isCountLoading: selectedClass !== undefined && pairs.isLoading,
    },
    { id: 'subjects', label: 'Subjects', count: subjects.firstPageMeta?.total },
  ];

  function renderClassesTable() {
    if (classes.error) {
      return (
        <ErrorState
          error={classes.error}
          fallbackTitle="Could not load the classes"
          onRetry={() => void classes.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={filteredClasses}
        columns={getQuestionClassColumns(handleClassOpen, {
          onEdit: (row) => {
            setEditingClass(row);
            setClassFormOpen(true);
          },
          onDelete: setRemovingClass,
        })}
        isLoading={classes.isLoading}
        className="lg:flex-1"
        stickyColumnId="name"
        emptyMessage="No class matches"
      />
    );
  }

  function renderClassPicker({ id, label, placeholder }: ClassPickerText) {
    return (
      <FilterFieldsGrid>
        <FilterField id={id} label={label}>
          <OptionSelect
            id={id}
            options={(classes.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
            value={search.classId}
            onChange={(classId) => void navigate({ search: (prev) => ({ ...prev, classId }) })}
            placeholder={placeholder}
            className={FIELD_CONTROL_CLASS}
            clearable
          />
        </FilterField>
      </FilterFieldsGrid>
    );
  }

  function renderClassSubjectsTable() {
    if (selectedClass === undefined) {
      return (
        <>
          {renderClassPicker(CLASS_SUBJECTS_PICKER)}
          <EmptyState
            kind="pickFirst"
            title="Pick a class"
            description="Choose a class above, or open one from the Classes tab."
            fill
          />
        </>
      );
    }
    if (pairs.error) {
      return (
        <ErrorState
          error={pairs.error}
          fallbackTitle="Could not load the subjects on this class"
          onRetry={() => void pairs.refetch()}
          fill
        />
      );
    }
    return (
      <>
        {renderClassPicker(CLASS_SUBJECTS_PICKER)}
        <DataTable
          data={pairs.data ?? []}
          columns={getClassSubjectColumns(setUnassigning)}
          isLoading={pairs.isLoading}
          className="lg:flex-1"
          stickyColumnId="subjectName"
          emptyMessage="No subject is on this class yet"
          emptyHint="Use Put on class on the Subjects tab."
        />
      </>
    );
  }

  function getPutOnClassHandler() {
    const { classId } = search;
    if (classId === undefined) return undefined;
    return (subject: QuestionSubject) => assign.mutate({ classId, subjectId: subject.id });
  }

  function renderSubjectsTable() {
    return (
      <>
        {renderClassPicker(PUT_ON_CLASS_PICKER)}

        {search.classId === undefined ? (
          <p className="text-sm text-muted-foreground">
            Pick a class above to put a subject on it.
          </p>
        ) : null}

        {subjects.loadError ? (
          <ErrorState
            error={subjects.loadError}
            fallbackTitle="Could not load the subjects"
            onRetry={() => void subjects.refetch()}
            fill
          />
        ) : (
          <DataTable
            data={subjects.rows}
            columns={getQuestionSubjectColumns(search.search, {
              onEdit: (row) => {
                setEditingSubject(row);
                setSubjectFormOpen(true);
              },
              onDelete: setRemovingSubject,
              onPutOnClass: getPutOnClassHandler(),
            })}
            isLoading={subjects.isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No subject matches"
            {...subjects.loadMoreProps}
          />
        )}
      </>
    );
  }

  function renderActiveTab() {
    if (activeTabId === 'classes') return renderClassesTable();
    if (activeTabId === 'classSubjects') return renderClassSubjectsTable();
    return renderSubjectsTable();
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Subject Management"
        search={{
          value: subjectSearch.searchText,
          onChange: subjectSearch.setSearchText,
          placeholder: 'Search classes and subjects',
        }}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => {
                setEditingClass(null);
                setClassFormOpen(true);
              }}
            >
              <Plus className="size-4" />
              Add Class
            </Button>
            <Button
              type="button"
              className="gap-2"
              onClick={() => {
                setEditingSubject(null);
                setSubjectFormOpen(true);
              }}
            >
              <Plus className="size-4" />
              Add Subject
            </Button>
          </>
        }
      />

      <TableTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onTabChange={setActiveTabId}
        ariaLabel="Subject tables"
      />

      {renderActiveTab()}

      <QuestionClassFormModal
        questionClass={editingClass}
        open={isClassFormOpen}
        onOpenChange={setClassFormOpen}
      />

      <QuestionSubjectFormModal
        subject={editingSubject}
        open={isSubjectFormOpen}
        onOpenChange={setSubjectFormOpen}
      />

      <ConfirmDialog
        open={removingClass !== null}
        onOpenChange={(open) => !open && setRemovingClass(null)}
        title="Remove this class?"
        description={
          removingClass === null
            ? ''
            : `"${removingClass.name}" carries ${removingClass.subjectCount} subjects; take those off first.`
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={deleteClass.isPending}
        onConfirm={() => removingClass && deleteClass.mutate(removingClass.id)}
      />

      <ConfirmDialog
        open={removingSubject !== null}
        onOpenChange={(open) => !open && setRemovingSubject(null)}
        title="Remove this subject?"
        description={
          removingSubject === null
            ? ''
            : `"${removingSubject.name}" cannot be removed while a class still carries it.`
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={deleteSubject.isPending}
        onConfirm={() => removingSubject && deleteSubject.mutate(removingSubject.id)}
      />

      <ConfirmDialog
        open={unassigning !== null}
        onOpenChange={(open) => !open && setUnassigning(null)}
        title="Take this subject off the class?"
        description={
          unassigning === null
            ? ''
            : `"${unassigning.subjectName}" will no longer be set for ${unassigning.className}.`
        }
        confirmLabel="Take off"
        confirmVariant="destructive"
        isPending={unassign.isPending}
        onConfirm={() => unassigning && unassign.mutate(unassigning.classSubjectId)}
      />
    </div>
  );
}
